import { createContext, type FC, type PropsWithChildren, useContext } from "react";

export type NavigationTarget = {
  pathname: string;
  search?: string;
  hash?: string;
};

/**
 * Router-agnostic navigation contract.
 *
 * Host applications own router-specific adapters and inject this contract through
 * {@link NavigationProvider}.
 */
export interface Navigation {
  /** Current logical pathname (e.g. `/entry`, `/login`, `/register`). */
  getPathname: () => string;
  /** Current query string, including the leading `?` when present. */
  getSearch: () => string;
  /** Current hash fragment, including the leading `#` when present. */
  getHash: () => string;
  /** Build an href from a structured target. */
  createHref: (target: NavigationTarget) => string;
  /** Push a new logical location onto the history stack. */
  push: (target: NavigationTarget) => void;
  /** Replace the current logical location. */
  replace: (target: NavigationTarget) => void;
  /** Navigate one entry back in history. */
  back: () => void;
}

const NavigationContext = createContext<Navigation | null>(null);

export const NavigationProvider: FC<PropsWithChildren<{ value: Navigation }>> = ({ value, children }) => (
  <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
);

export const useNavigation = (): Navigation => {
  const navigation = useContext(NavigationContext);

  if (!navigation) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }

  return navigation;
};
